// If the isInstantSearch prop is true, the search will be triggered on every filter change. If it's false, the search will be triggered when the user clicks the search button.

import { Button, Checkbox, DatePicker, Form, Input, InputNumber, Radio, Select, Slider, Space, Switch, Tooltip } from "antd"
import { isBoolean, isEmpty } from "lodash"
import moment from "moment"
import React, { useEffect, useImperativeHandle, useRef, useState } from "react"
import { t } from "../../languages/i18n"
import { filterOption } from "../../lib/external-components"
import dayjs, { Dayjs } from "dayjs";

export type RadioOption = {
  value: string | number
  label: string
  defaultChecked?: boolean
  disabled?: boolean
}
export const { RangePicker } = DatePicker

export interface FilterComponent {
  type:
    | "text"
    | "select"
    | "date"
    | "number"
    | "range"
    | "checkbox"
    | "radio"
    | "switch"
    | "select-multiple"
    | "live-search"
    | "range_picker"
    | "debounce-search"
    | "select-search"
    | "select-lazy-search"
  name: string
  label: string
  options?: string[] | number[] | object[] | RadioOption[]
  placeholder?: string
  defaultValue?: any
  required?: boolean
  disabled?: boolean
  hidden?: boolean
  typeInputNumber?: boolean
  onChange?: (value: any) => void
  onBlur?: (value: any) => void
  picker?: "time" | "date" | "month" | "week" | "quarter" | "year"
  customStyle?: React.CSSProperties | string
  onSearch?: (value: string) => void
  onPopupScroll?: (e: React.UIEvent<HTMLDivElement>) => void
  loading?: boolean
}

export interface FiltersProps {
  filters: FilterComponent[]
  onSubmit: (filters: object) => void
  isInstantSearch?: boolean
  isClearFilter?: boolean
  onSearch?: (filters: object) => void
  filterRef: React.Ref<{ filterFunction: () => void }>
  defaultFilterValues?: { [key: string]: string | Dayjs | null } | null
  customStyle?: React.CSSProperties | string
  onClearFilters?: () => void
  onLocaleClear?: () => void
}

const Filters: React.FC<FiltersProps> = ({
  filters,
  onSubmit,
  isInstantSearch = true,
  isClearFilter = false,
  onSearch,
  filterRef,
  defaultFilterValues,
  customStyle,
  onClearFilters,
  onLocaleClear,
}) => {
  const [defaultValues, setDefaultValues] = useState<object>({})
  const [filterValues, setFilterValues] = useState<object | null>(null)
  const [searchValues, setSearchValues] = useState<object>({})
  const [timeRangeValue, setTimeRangeValue] = useState<string>("")
  const [form] = Form.useForm()
  const clearFilterRef = useRef<{ filterFunction: () => void } | null>(null)
  const skipNextInstantSubmitRef = useRef(false)
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const handleInputChange = (name: string, value: any) => {
    if (name === "timeRange") {
      setTimeRangeValue(value || "")
    }

    setFilterValues(prevValues => {
      // Check if value is null, undefined, or empty string (but not moment object)
      if (!isBoolean(value) && !value && !moment.isMoment(value)) {
        // @ts-expect-error
        delete prevValues[name]
        return { ...prevValues }
      }

      return {
        ...prevValues,
        [name]: value,
      }
    })
  }

  const handleInputSearch = (name: string, value: any) => {
    setSearchValues(prevValues => ({ ...prevValues, [name]: value }))
  }

  const handleClearFilters = (values: object) => {
    setSearchValues({})
    setFilterValues({ ...defaultValues })
    form.resetFields()
    form.setFieldsValue({ ...defaultValues })
  }

  useEffect(() => {
    clearFilterRef.current = { filterFunction: () => handleClearFilters(defaultValues) }
  }, [defaultValues])

  useImperativeHandle(filterRef, () => ({
    filterFunction: () => {
      if (clearFilterRef.current && clearFilterRef.current.filterFunction) {
        clearFilterRef.current.filterFunction()
      }
    },
  }))

  const handleSubmitFilters = () => {
    // Lấy giá trị từ form (cho các field khác) và filterValues (cho dates)
    const formValues = form.getFieldsValue()
    const finalValues = { ...formValues, ...filterValues }
    // Format date values before submit
    const formattedValues = {
      ...finalValues,
      from: formValues.fromDate ? dayjs(formValues.fromDate).startOf('day').format("YYYY-MM-DD HH:mm:ss") : "",
      to: formValues.toDate ? dayjs(formValues.toDate).endOf('day').format("YYYY-MM-DD HH:mm:ss") : "",
      range: finalValues.timeRange || "",
      created_by: finalValues.author || "",
      website: finalValues.website || "",
    }

    onSubmit(formattedValues)
  }

  const renderFilterComponent = (filter: FilterComponent) => {
    switch (filter.type) {
      case "text":
        return (
          <Input
            key={filter.name}
            className={filter.customStyle as string}
            placeholder={filter.placeholder}
            onChange={e => {
              clearTimeout(debounceTimers.current[filter.name])
              debounceTimers.current[filter.name] = setTimeout(() => {
                handleInputChange(filter.name, e.target.value)
              }, 400)
            }}
          />
        )
      case "debounce-search":
        return (
          <Tooltip title={filter.placeholder} placement="top">
            <Input
              key={filter.name}
              style={{ minWidth: 260 }}
              className={typeof filter.customStyle === "string" ? filter.customStyle : undefined}
              placeholder={filter.placeholder}
              onChange={e => {
                clearTimeout(debounceTimers.current[filter.name])
                debounceTimers.current[filter.name] = setTimeout(() => {
                  handleInputChange(filter.name, e.target.value)
                }, 500)
              }}
            />
          </Tooltip>
        )
      case "select":
        return (
          <Select
            className={filter.customStyle as string}
            key={filter.name}
            placeholder={filter.placeholder}
            allowClear
            onChange={value => handleInputChange(filter.name, value)}
            onClear={() => {
              // Nếu là locale select và có onLocaleClear, gọi nó
              if (filter.name === "locale" && onLocaleClear) {
                onLocaleClear()
              } else {
                // Khi clear select khác, reset về giá trị mặc định
                const defaultValue = filter.defaultValue || ""
                handleInputChange(filter.name, defaultValue)
              }
            }}
          >
            {(filter.options as RadioOption[]).map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        )

      case "date":
        const isTimeRangeSelected = timeRangeValue && timeRangeValue !== ""
        const shouldDisableDate =
          (filter.name === "fromDate" || filter.name === "toDate") && Boolean(isTimeRangeSelected)

        return (
          <DatePicker
            className={filter.customStyle as string}
            key={filter.name}
            style={{ width: 160 }}
            placeholder={filter.placeholder}
            disabled={filter.disabled || shouldDisableDate}
            disabledDate={(current) => {
              if (!current) return false;

              const isFuture = current.isAfter(dayjs(), 'day');
              if (isFuture) return true;

              const fromDate = form.getFieldValue("fromDate");
              const toDate = form.getFieldValue("toDate");

              if (filter.name === "toDate") {
                return fromDate ? current.isBefore(fromDate, 'day') : false;
              }

              if (filter.name === "fromDate") {
                return toDate ? current.isAfter(toDate, 'day') : false;
              }

              return false;
            }}
            onChange={date => handleInputChange(filter.name, date ? date.toISOString() : null)}
            picker={filter.picker}
            format={filter.picker === "month" ? "MM/YYYY" : "DD/MM/YYYY"}
          />
        )

      case "number":
        return (
          <InputNumber
            className={filter.customStyle as string}
            key={filter.name}
            onChange={value => {
              clearTimeout(debounceTimers.current[filter.name])
              debounceTimers.current[filter.name] = setTimeout(() => {
                handleInputChange(filter.name, value)
              }, 400)
            }}
            formatter={(value: any) =>
              filter.typeInputNumber ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : value
            }
            step={10000}
            min={0}
          />
        )
      case "range":
        return (
          <Slider
            key={filter.name}
            className={filter.customStyle as string}
            range
            onChange={value => handleInputChange(filter.name, value)}
          />
        )
      case "checkbox":
        return (
          <Checkbox.Group
            key={filter.name}
            className={filter.customStyle as string}
            options={(filter.options as RadioOption[])?.map((option: { value: string | number; label: string }) => ({
              label: option.label,
              value: option.value,
            }))}
            onChange={values => handleInputChange(filter.name, values)}
          />
        )
      case "radio":
        return (
          <Radio.Group
            className={filter.customStyle as string}
            key={filter.name}
            options={(filter.options as RadioOption[])?.map((option: { value: string | number; label: string }) => ({
              label: option.label,
              value: option.value,
            }))}
            onChange={e => handleInputChange(filter.name, e.target.value)}
          />
        )
      case "switch":
        return (
          <Switch
            className={filter.customStyle as string}
            key={filter.name}
            defaultChecked={filter.defaultValue}
            onChange={checked => handleInputChange(filter.name, checked)}
          />
        )
      case "select-multiple":
        return (
          <Select key={filter.name} mode="multiple" onChange={values => handleInputChange(filter.name, values)}>
            {(filter.options as string[])?.map((option: string | number) => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        )
      case "live-search":
        return (
          <Select
            key={filter.name}
            className={filter.customStyle as string}
            showSearch
            onSearch={value => handleInputSearch(filter.name, value)}
            onChange={value => handleInputChange(filter.name, value)}
            filterOption={false}
            options={(filter.options as RadioOption[])?.map((option: { value: string | number; label: string }) => ({
              label: option.label,
              value: option.value,
            }))}
            style={{ minWidth: "14rem" }}
            allowClear
            onClear={() => {
              handleInputSearch(filter.name, "")
              handleInputChange(filter.name, "")
            }}
          />
        )
      case "select-search":
        return (
          <Select
            className={filter.customStyle as string}
            key={filter.name}
            showSearch
            onSearch={value => handleInputSearch(filter.name, value)}
            onChange={value => handleInputChange(filter.name, value)}
            style={{ minWidth: "14rem" }}
            placeholder={filter.placeholder}
            optionFilterProp="children"
            filterOption={filterOption}
            options={(filter.options as RadioOption[])?.map((option: { value: string | number; label: string }) => ({
              label: option.label,
              value: option.value.toString(),
            }))}
            allowClear
            onClear={() => {
              handleInputSearch(filter.name, "")
              // Khi clear select-search, reset về giá trị mặc định
              const defaultValue = filter.defaultValue || ""
              handleInputChange(filter.name, defaultValue)
            }}
          />
        )
      case "select-lazy-search":
        return (
          <Select
            className={filter.customStyle as string}
            key={filter.name}
            showSearch
            filterOption={false}
            onSearch={filter.onSearch}
            onChange={value => handleInputChange(filter.name, value)}
            onPopupScroll={filter.onPopupScroll}
            style={{ minWidth: "14rem" }}
            placeholder={filter.placeholder}
            options={(filter.options as RadioOption[])?.map((option: { value: string | number; label: string }) => ({
              label: option.label,
              value: option.value.toString(),
            }))}
            loading={filter.loading}
            allowClear
            onClear={() => {
              filter.onSearch?.("")
              handleInputChange(filter.name, "")
            }}
          />
        )
      default:
        return null
    }
  }

  useEffect(() => {
    let defaultFilter: { [key: string]: any } = {}
    filters.forEach(filter => {
      if (!isBoolean(filter.defaultValue) && !filter.defaultValue) {
        return
      }
      defaultFilter = { ...defaultFilter, [filter.name]: filter.defaultValue }
    })
    if (!isEmpty(defaultFilter)) {
      setDefaultValues({ ...defaultFilter })
      // setFilterValues({ ...defaultFilter })
    }
  }, [])

  useEffect(() => {
    if (isInstantSearch && filterValues) {
      if (skipNextInstantSubmitRef.current) {
        skipNextInstantSubmitRef.current = false
        return
      }
      onSubmit(filterValues)
    }
  }, [filterValues, isInstantSearch])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    onSearch && onSearch(searchValues)
  }, [searchValues])

  useEffect(() => {
    if (defaultFilterValues) {
      // Syncing defaults from parent should not trigger an instant submit cycle.
      skipNextInstantSubmitRef.current = true
      setFilterValues({ ...defaultFilterValues })
      form.setFieldsValue({ ...defaultFilterValues })
    }
  }, [defaultFilterValues])

  return (
    <Form
      name="customized_form_controls"
      layout="inline"
      style={{ rowGap: "1rem", flexWrap: "wrap", alignItems: "flex-end" }}
      form={form}
    >
      <Space size={12} className="flex flex-wrap items-end">
        {filters.map(filter => {
          const isDateField = filter.name === "fromDate" || filter.name === "toDate"

          return (
            <div key={filter.name}>
              <Form.Item
                name={filter.name}
                label={filter.label}
                initialValue={getFilterInitialValue(filter.type, filter.defaultValue)}
                className="mb-0"
              >
                {renderFilterComponent(filter)}
              </Form.Item>
            </div>
          )
        })}

        {!isInstantSearch && (
          <Form.Item className="mb-0">
            <Button className="bg-[#37B24D] text-white" onClick={handleSubmitFilters} style={{ height: "32px" }}>
              {t("Find")}
            </Button>
          </Form.Item>
        )}

        {isClearFilter && (
          <Form.Item className="mb-0">
            <Button onClick={onClearFilters || handleClearFilters}>{t("Clear search")}</Button>
          </Form.Item>
        )}
      </Space>
    </Form>
  )
}

function getFilterInitialValue(
  type:
    | "text"
    | "select"
    | "date"
    | "number"
    | "range"
    | "checkbox"
    | "radio"
    | "switch"
    | "select-multiple"
    | "live-search"
    | "range_picker"
    | "debounce-search"
    | "select-search"
    | "select-lazy-search",
  value: any,
) {
  if (type === "date") {
    if (!value) return null
    if (typeof value === "string") {
      return moment(value) // Handle ISO string
    }
    return value
  }

  return value
}

export default Filters
